import { useState, useEffect, useCallback, useId, type ReactNode } from 'react';
import { Link as RouterLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  IconButton,
  Link,
  Button,
  Card,
  CardContent,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  Grid,
  InputLabel,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import RunTestIcon from '@mui/icons-material/PlayCircleOutline';
import ViewIcon from '@mui/icons-material/Visibility';
import CopyIcon from '@mui/icons-material/ContentCopy';
import ImportIcon from '@mui/icons-material/CloudUpload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import {
  contextService,
  CONTEXT_TYPE_VALUES,
  Context,
  ContextStats,
  ContextType,
} from '@services/contextService';
import { testService, ScanRunListItem } from '@services/testService';
import { organizationService } from '@services/organizationService';
import { apiKeyService } from '@services/apiKeyService';
import ScopeInventoryTab from '@components/context/ScopeInventoryTab';
import AuditsTab from '@components/context/AuditsTab';
import IntegrationTargetCard from '@components/context/IntegrationTargetCard';
import { apiErrorStatus } from '../utils/apiError';
import ContextTestConfigSection from '@components/testconfig/ContextTestConfigSection';
import ContextCrawlConfigSection from '@components/crawlconfig/ContextCrawlConfigSection';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { compactRelativeTime } from '@utils/relativeTime';
import { visuallyHidden } from '@mui/utils';
import { RunSourceSummary } from '../components/runs/RunSource';
import { DiscoverySummary } from '../components/runs/DiscoveryOutcome';
import DensityTrendCard from '../components/runs/DensityTrendCard';
import FindingsTrendPanel from '../components/findings/FindingsTrendPanel';
import { useBreadcrumbLabel } from '@store/breadcrumbLabels';
import { formatDateTime as formatDateTime0 } from '@utils/format';
import { invalidOriginLines, originsToSend, originsToText } from '@utils/allowedOrigins';

const updateContextSchema = z.object({
  name: z
    .string()
    .min(1, 'trackedPlaces.validation.nameRequired')
    .max(255, 'trackedPlaces.validation.nameTooLong'),
  description: z.string().optional(),
  // REV-502: Type and Identifier are editable. Bounds mirror the API validator
  // (enum vocabulary, 1..500) so the client rejects what the server would.
  context_type: z.enum(CONTEXT_TYPE_VALUES),
  canonical_identifier: z
    .string()
    .min(1, 'trackedPlaces.validation.identifierRequired')
    .max(500, 'trackedPlaces.validation.identifierTooLong'),
  // REV-810: the extra origins this place accepts evidence for, edited as one
  // origin per line. The API normalizes and is the authority on what an origin
  // is; this check only says which line to fix before the request goes.
  allowed_origins: z
    .string()
    .refine((text) => invalidOriginLines(text).length === 0, {
      message: 'trackedPlaces.validation.allowedOriginInvalid',
    })
    .refine((text) => (originsToSend(text) ?? []).length <= MAX_ALLOWED_ORIGINS, {
      message: 'trackedPlaces.validation.allowedOriginsTooMany',
    }),
});

/**
 * REV-810: the types this place may be changed to — the two a place can be
 * registered as, plus whatever it already is.
 *
 * A deployment is bounded exactly as a website is, so it is no longer offered
 * when registering one (see ContextsTab); an aggregation is not something a
 * person creates at all (REV-494). Neither may be *picked* here, but a place
 * that already is one keeps its own type in the list, or opening Settings would
 * silently re-type it on the next save.
 *
 * @param current - the place's stored type, while it is still loading undefined
 */
function typeOptions(current: ContextType | undefined): ContextType[] {
  const offered: ContextType[] = ['repository', 'website'];
  return current && !offered.includes(current) ? [...offered, current] : offered;
}

/**
 * REV-810: the same ceiling the API validates (revenant-api
 * `src/utils/scopeBounds.js`, `MAX_ALLOWED_ORIGINS`). A place with more
 * addresses than this is more than one place.
 */
const MAX_ALLOWED_ORIGINS = 20;

type UpdateContextFormData = z.infer<typeof updateContextSchema>;

const getStatusColor = (status: string): 'success' | 'warning' | 'default' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'paused':
      return 'warning';
    default:
      return 'default';
  }
};

const getScanStatusColor = (
  status: string
): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'info';
    case 'cancelled':
      return 'warning';
    default:
      return 'default';
  }
};

const formatDuration = (ms: number | null | undefined): string => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatDate = (dateStr: string | null | undefined): string => {
  return formatDateTime0(dateStr, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    fallback: '-',
  });
};

/**
 * A tab panel whose shell (with role/id/aria-labelledby) is ALWAYS mounted so
 * each tab's aria-controls references a real element, while its content stays
 * lazy — only mounted when the panel is active. This keeps the WAI tabs pattern
 * intact without eagerly running each tab's data fetches.
 */
// REV-497: URL segment per tab, so a tracked place's tabs are linkable and
// survive a reload. Order must match the <Tab> order below.
// REV-929: 'audits' makes /contexts/:id/audits — the breadcrumb above the
// audit initiation page — land on this place's audit list.
/** The API reference revenant-api serves (REV-148), same origin as the app. */
const API_REFERENCE_URL = '/api/docs/';

const TAB_NAMES = ['overview', 'scope', 'audits', 'integrate', 'settings'] as const;
type TabName = (typeof TAB_NAMES)[number];

function getTabIndex(tabName: string | null): number {
  if (!tabName) return 0;
  const index = TAB_NAMES.indexOf(tabName as TabName);
  return index >= 0 ? index : 0;
}

function TabPanel({
  index,
  active,
  children,
}: {
  index: number;
  active: number;
  children: ReactNode;
}) {
  const hidden = active !== index;
  return (
    <Box
      role="tabpanel"
      id={`ctx-tabpanel-${index}`}
      aria-labelledby={`ctx-tab-${index}`}
      hidden={hidden}
    >
      {!hidden && children}
    </Box>
  );
}

function ContextDetailPage() {
  const { t } = useTranslation();
  const { slug, contextId } = useParams<{ slug: string; contextId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // useId, not a fixed string: the same reasoning OrgSwitcher records — a
  // hardcoded DOM id becomes a duplicate the moment this renders twice, and a
  // duplicate id silently points aria-describedby at the wrong element.
  const PAUSED_WARNING_ID = useId();

  // Core state
  const [orgId, setOrgId] = useState<string | null>(null);
  const [context, setContext] = useState<Context | null>(null);
  const [loading, setLoading] = useState(true);
  // REV-939: the breadcrumb names the place, not its title-cased slug or
  // "Details". Keyed by the segment as the URL has it (slug or id), which is
  // the path the crumb links to; a rename updates it with `context`.
  // `crumbLoadedFor` is the path `context` was fetched for: the page stays
  // mounted when only :contextId changes, and until the next place loads
  // `context` is the previous one, whose name must not reach the new crumb.
  const [crumbLoadedFor, setCrumbLoadedFor] = useState<string | null>(null);
  useBreadcrumbLabel(
    slug && contextId ? `/orgs/${slug}/contexts/${contextId}` : undefined,
    context && crumbLoadedFor ? { path: crumbLoadedFor, label: context.name } : null
  );
  const [error, setError] = useState<string | null>(null);

  /**
   * REV-497: the URL segment may now be a SLUG. Only the context lookup itself
   * accepts one — every sub-resource endpoint, mutation and child component
   * still keys off the real UUID. Resolve once, then act on the canonical id,
   * exactly as the API does server-side. Using the raw param downstream would
   * quietly query for a context that does not exist and render empty state
   * instead of failing.
   */
  const resolvedId = context?.id ?? null;

  /**
   * REV-497: tab state lives in the URL, not in useState.
   *
   * The bookmarkability audit this ticket asked for found that it did not:
   * every reload dumped the user back on Overview, and there was no way to
   * link a colleague to a specific tab. OrganizationDetailPage already solves
   * this the same way (REV-486), so this mirrors it rather than inventing a
   * second pattern.
   */
  const pathParts = location.pathname.split('/');
  const tabFromPath = pathParts.length > 5 ? (pathParts[5] ?? null) : null;
  const activeTab = getTabIndex(tabFromPath);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    void navigate(`/orgs/${slug}/contexts/${contextId}/${TAB_NAMES[newValue]}`);
  };

  // Stats state
  const [stats, setStats] = useState<ContextStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Test history state
  const [scanRuns, setScanRuns] = useState<ScanRunListItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState<string>('');

  // Integrate tab state
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Settings tab state
  const [saving, setSaving] = useState(false);
  /**
   * REV-700: how many API keys the organization already has.
   *
   * null means "not known yet, or the lookup failed". That is a third state on
   * purpose: the Integrate notice used to assert unconditionally that the user
   * needs to CREATE a key, which is wrong for anyone who already has one — and
   * guessing the other way when the lookup fails would be equally wrong. When
   * we do not know, the copy says what is true regardless.
   */
  const [apiKeyCount, setApiKeyCount] = useState<number | null>(null);
  const [orgRole, setOrgRole] = useState<string | null>(null);

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    formState: { errors: formErrors },
  } = useForm<UpdateContextFormData>({
    resolver: zodResolver(updateContextSchema),
    defaultValues: {
      name: '',
      description: '',
      // Placeholders only — reset() replaces these the moment the context
      // loads. zod requires a member of the enum, so 'website' stands in.
      context_type: 'website',
      canonical_identifier: '',
      allowed_origins: '',
    },
  });

  // Fetch context
  const fetchContext = useCallback(async () => {
    if (!slug || !contextId) return;

    setLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrgId(org.id);
      // REV-700: getBySlug returns the caller's own role (REV-523). Needed
      // because the API-key lookup below is admin-only.
      setOrgRole(org.role ?? null);
      const ctx = await contextService.getById(org.id, contextId);
      setContext(ctx);
      setCrumbLoadedFor(`/orgs/${slug}/contexts/${contextId}`);
      reset({
        name: ctx.name,
        description: ctx.description || '',
        context_type: ctx.context_type,
        canonical_identifier: ctx.canonical_identifier,
        allowed_origins: originsToText(ctx.allowed_origins),
      });
    } catch {
      setError(t('trackedPlaces.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [slug, contextId, t, reset]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!orgId || !resolvedId) return;

    setStatsLoading(true);
    try {
      const data = await contextService.getStats(orgId, resolvedId);
      setStats(data);
    } catch {
      // Stats are non-critical; fail silently
    } finally {
      setStatsLoading(false);
    }
  }, [orgId, resolvedId]);

  /**
   * REV-700: does this organization already have API keys?
   *
   * Non-critical, and it fails silently like fetchStats above: a failed lookup
   * leaves apiKeyCount null, which the Integrate notice renders as neutral copy
   * rather than as a confident claim in either direction.
   */
  const fetchApiKeyCount = useCallback(async () => {
    if (!orgId) return;
    // GET /organizations/:orgId/api-keys is requireOrgRole('admin'), which per
    // ROLE_HIERARCHY means owner or admin. Asking as anyone else is a request
    // that is GUARANTEED to 403 on every load of this tab — log noise and a
    // stream of denials in the API's audit trail, for an answer we would throw
    // away anyway. Members simply keep the neutral copy, which is the correct
    // thing to show someone who cannot see the key inventory.
    if (orgRole !== 'owner' && orgRole !== 'admin') {
      setApiKeyCount(null);
      return;
    }
    try {
      const keys = await apiKeyService.list(orgId);
      setApiKeyCount(Array.isArray(keys) ? keys.length : 0);
    } catch {
      setApiKeyCount(null);
    }
  }, [orgId, orgRole]);

  // Fetch test history
  const fetchHistory = useCallback(async () => {
    if (!orgId || !resolvedId) return;

    setHistoryLoading(true);
    try {
      const params: Record<string, unknown> = {
        contextId: resolvedId,
        organizationId: orgId,
        page: historyPage,
        limit: 10,
      };
      if (sourceFilter) {
        params.scanType = sourceFilter;
      }
      const data = await testService.listScanRuns(params);
      setScanRuns(data.items ?? []);
      setHistoryTotal(data.pagination.total);
    } catch {
      // History fetch failure is non-critical
    } finally {
      setHistoryLoading(false);
    }
  }, [orgId, resolvedId, historyPage, sourceFilter]);

  useEffect(() => {
    void fetchContext();
  }, [fetchContext]);

  useEffect(() => {
    if (orgId) {
      void fetchStats();
      void fetchHistory();
      void fetchApiKeyCount();
    }
  }, [orgId, fetchStats, fetchHistory, fetchApiKeyCount]);

  // Handlers
  const handleSave = async (data: UpdateContextFormData) => {
    if (!orgId || !resolvedId) return;

    setSaving(true);
    setSaveSuccess(false);
    try {
      const updatedContext = await contextService.update(orgId, resolvedId, {
        name: data.name,
        // REV-956: null clears it. Omitting the key would keep the old text.
        description: data.description || null,
        context_type: data.context_type,
        canonical_identifier: data.canonical_identifier,
        // REV-810: null clears the list, so an emptied editor really empties it.
        allowed_origins: originsToSend(data.allowed_origins),
      });
      setContext(updatedContext);
      reset({
        name: updatedContext.name,
        description: updatedContext.description || '',
        context_type: updatedContext.context_type,
        canonical_identifier: updatedContext.canonical_identifier,
        allowed_origins: originsToText(updatedContext.allowed_origins),
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      // REV-502: a 409 means another tracked place in this org already uses
      // that identifier. Say so, rather than the generic failure message —
      // the user can act on a name clash but not on "update failed".
      setError(
        apiErrorStatus(err) === 409
          ? t('trackedPlaces.errors.identifierTaken')
          : t('trackedPlaces.errors.updateFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async () => {
    if (!orgId || !resolvedId) return;
    setStatusChanging(true);
    try {
      const updatedContext = await contextService.pause(orgId, resolvedId);
      setContext(updatedContext);
    } catch {
      setError(t('trackedPlaces.errors.pauseFailed'));
    } finally {
      setStatusChanging(false);
    }
  };

  const handleActivate = async () => {
    if (!orgId || !resolvedId) return;
    setStatusChanging(true);
    try {
      const updatedContext = await contextService.activate(orgId, resolvedId);
      setContext(updatedContext);
    } catch {
      setError(t('trackedPlaces.errors.activateFailed'));
    } finally {
      setStatusChanging(false);
    }
  };

  const handleDelete = async () => {
    if (!orgId || !resolvedId) return;
    setDeleting(true);
    try {
      await contextService.delete(orgId, resolvedId);
      void navigate(`/orgs/${slug}/contexts`);
    } catch {
      setError(t('trackedPlaces.errors.deleteFailed'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleCopyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  // REV-732: a destination rather than a handler, so Run test can be a link.
  const runTestHref = (() => {
    const params = new URLSearchParams();
    if (orgId) params.set('orgId', orgId);
    if (resolvedId) params.set('contextId', resolvedId);
    return `/tests/new?${params.toString()}`;
  })();

  /**
   * REV-732: Run test is disabled while the place is paused, and the rule's
   * second half applies — a control that WOULD navigate but currently cannot
   * is a real <button aria-disabled>, not a link with a dead href. Same shape
   * as AuditInitiationPage's Cancel and NavigationDrawer's unavailable items.
   *
   * aria-disabled rather than the `disabled` attribute so the control keeps its
   * place in the tab order: a keyboard user should be able to reach it and be
   * told why it is unavailable, not have it vanish.
   *
   * Both halves of that promise have to be kept, and neither is free:
   *
   * 1. `aria-describedby` points at the paused Alert below, so "told why" is
   *    actually true. NavigationDrawer establishes this pairing (REV-679) and
   *    CLAUDE.md requires it — aria-disabled without an explanation reaches a
   *    control that still cannot say anything. The Alert is used rather than a
   *    visually-hidden copy because it is already on screen saying exactly the
   *    right sentence, and one string beats two that can drift apart. It is
   *    rendered under the same `status === 'paused'` condition that disables
   *    these buttons, so the reference is never dangling.
   *
   * 2. The disabled LOOK has to be restored by hand. MUI attaches Mui-disabled
   *    — and with it the grey — only for the `disabled` prop, so dropping that
   *    attribute silently left a full-strength contained-primary CTA that
   *    ripples on click and does nothing. Measured, not assumed. `sx` rather
   *    than the Mui-disabled class: that class also sets pointer-events: none,
   *    which would take the cursor and any future tooltip away from the very
   *    users the explanation is for.
   */
  const runTestLinkProps = (status: Context['status']) =>
    status === 'active'
      ? ({ component: RouterLink, to: runTestHref } as const)
      : ({
          component: 'button' as const,
          'aria-disabled': true,
          // The status itself, not a boolean, precisely so this cannot drift
          // from the Alert's own condition. The control is disabled for every
          // status except 'active', but the Alert renders only for 'paused' —
          // so a boolean parameter let the describedby point at an element
          // that is not on the page when the place is 'removed'. A dangling
          // aria-describedby announces NOTHING, which is the very defect this
          // attribute was added to fix, just relocated to a rarer status.
          'aria-describedby': status === 'paused' ? PAUSED_WARNING_ID : undefined,
          sx: {
            color: 'action.disabled',
            backgroundColor: 'action.disabledBackground',
            boxShadow: 'none',
            cursor: 'default',
            '&:hover': {
              backgroundColor: 'action.disabledBackground',
              boxShadow: 'none',
            },
          },
        } as const);

  // Loading skeleton
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="text" width={150} height={24} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  // Error state (no context loaded)
  if (error && !context) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Link
          component={RouterLink}
          to={`/orgs/${slug}/contexts`}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ArrowBackIcon sx={{ mr: 1 }} />
          {t('trackedPlaces.title')}
        </Link>
      </Box>
    );
  }

  if (!context) return null;

  const canDelete = deleteConfirmText === context.name;

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            component={RouterLink}
            to={`/orgs/${slug}/contexts`}
            sx={{ mr: 2 }}
            aria-label={t('trackedPlaces.title')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" component="h1" fontWeight={700}>
                {context.name}
              </Typography>
              <Chip
                label={t(`trackedPlaces.status.${context.status}`)}
                color={getStatusColor(context.status)}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {context.canonical_identifier}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<RunTestIcon />}
            {...runTestLinkProps(context.status)}
          >
            {t('trackedPlaces.history.runTest')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            component={RouterLink}
            to={`/orgs/${slug}/contexts/${resolvedId}/import-history`}
          >
            {t('importHistory.title')}
          </Button>
          {/* REV-512: a tracked place had no route to its own findings in the
              standard list presentation — only an aggregate report, test
              history, and count-only stat cards. Reaching findings meant going
              to the org-wide list and re-selecting this place by hand. */}
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            component={RouterLink}
            to={`/orgs/${slug}/findings?context_id=${resolvedId}`}
          >
            {t('trackedPlaces.viewFindings', 'View findings')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            component={RouterLink}
            to={`/orgs/${slug}/contexts/${resolvedId}/reports/issues-breakdown`}
          >
            {t('reports.issuesBreakdown.navLabel')}
          </Button>
          {context.status === 'active' ? (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<PauseIcon />}
              onClick={handlePause}
              disabled={statusChanging}
            >
              {statusChanging ? <CircularProgress size={20} /> : t('trackedPlaces.actions.pause')}
            </Button>
          ) : context.status === 'paused' ? (
            <Button
              variant="outlined"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={handleActivate}
              disabled={statusChanging}
            >
              {statusChanging ? (
                <CircularProgress size={20} />
              ) : (
                t('trackedPlaces.actions.activate')
              )}
            </Button>
          ) : null}
        </Box>
      </Box>

      {context.status === 'paused' && (
        <Alert severity="warning" sx={{ mb: 3 }} id={PAUSED_WARNING_ID}>
          {t('trackedPlaces.warnings.paused')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Summary Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 2,
          mb: 3,
        }}
      >
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" component="p" fontWeight={700}>
              {statsLoading ? (
                <Skeleton width={40} sx={{ mx: 'auto' }} />
              ) : (
                (stats?.totalScans ?? 0)
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('trackedPlaces.overview.totalScans')}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" component="p" fontWeight={700}>
              {statsLoading ? (
                <Skeleton width={40} sx={{ mx: 'auto' }} />
              ) : (
                (stats?.totalFindings ?? 0)
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('trackedPlaces.overview.totalFindings')}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" component="p" fontWeight={700} color="error.main">
              {statsLoading ? (
                <Skeleton width={40} sx={{ mx: 'auto' }} />
              ) : (
                (stats?.findingsBySeverity?.critical ?? 0)
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('trackedPlaces.overview.critical')}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" component="p" fontWeight={700} color="warning.main">
              {statsLoading ? (
                <Skeleton width={40} sx={{ mx: 'auto' }} />
              ) : (
                (stats?.findingsBySeverity?.major ?? 0)
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('trackedPlaces.overview.major')}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            {/* REV-577: h4/700 to match the four sibling cards exactly. That
                is only possible because the value is now compact — measured
                in-browser, the full timestamp is 375px at h4 against a card
                whose grid minimum is 160px, so it wrapped across ~3 lines and
                looked worse than the size mismatch being fixed. The exact
                timestamp moves to `title`, so nothing is lost. */}
            <Typography
              variant="h4"
              component="p"
              fontWeight={700}
              // `title` is a MOUSE affordance: unreachable by keyboard and
              // unreliably announced. The exact time used to be visible text,
              // so it must stay available to assistive tech — but NOT via
              // aria-label: this is a <p>, which has no role, and aria-label on
              // a roleless element is invalid ARIA (ARIA-40, the same rule
              // REV-540 hit on a MUI Chip). Our own engine rejected it.
              //
              // A visually-hidden span is the valid form and matches the
              // idiom already used in OrgSwitcher and OrgFindingsPage.
              title={stats?.lastScanAt ? formatDate(stats.lastScanAt) : undefined}
            >
              {statsLoading ? (
                <Skeleton width={40} sx={{ mx: 'auto' }} />
              ) : (
                compactRelativeTime(stats?.lastScanAt)
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('trackedPlaces.overview.lastScan')}
              {/* The exact timestamp, for assistive tech. It lives on THIS
                  line, not on the value above: that one is styled like a
                  heading (h4/700) without being one, and giving it substantial
                  text trips STRUCTURE-07 — our own engine caught it. Sighted
                  users get the same value via `title` on hover. */}
              {stats?.lastScanAt && (
                <Box component="span" sx={visuallyHidden}>
                  {' '}
                  {formatDate(stats.lastScanAt)}
                </Box>
              )}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs: Overview / Scope / Audits / Integrate / Settings */}
      <Tabs
        // REV-725: standard tabs clip rather than reflow. Scrollable keeps
        // every section reachable at any width.
        variant="scrollable"
        scrollButtons="auto"
        value={activeTab}
        onChange={handleTabChange}
        aria-label={t('trackedPlaces.tabs.ariaLabel', 'Tracked place sections')}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', maxWidth: '100%' }}
      >
        {/* Panels are permanently-mounted shells (see TabPanel), so every tab
            can safely reference its panel via aria-controls. */}
        <Tab
          label={t('trackedPlaces.tabs.overview')}
          id="ctx-tab-0"
          aria-controls="ctx-tabpanel-0"
        />
        <Tab
          label={t('trackedPlaces.tabs.scope', 'Scope Inventory')}
          id="ctx-tab-1"
          aria-controls="ctx-tabpanel-1"
        />
        <Tab label={t('trackedPlaces.tabs.audits')} id="ctx-tab-2" aria-controls="ctx-tabpanel-2" />
        <Tab
          label={t('trackedPlaces.tabs.integrate')}
          id="ctx-tab-3"
          aria-controls="ctx-tabpanel-3"
        />
        <Tab
          label={t('trackedPlaces.tabs.settings')}
          id="ctx-tab-4"
          aria-controls="ctx-tabpanel-4"
        />
      </Tabs>

      {/* Overview Tab */}
      <TabPanel index={0} active={activeTab}>
        {/* REV-141: nothing to chart before the first run, and the empty state's
            "run your first test" should not sit under two empty charts. */}
        {(stats?.totalScans ?? 0) > 0 && orgId && resolvedId && (
          <>
            <FindingsTrendPanel orgId={orgId} contextId={resolvedId} />
            <Grid container sx={{ mb: 2 }}>
              <DensityTrendCard orgId={orgId} contextId={resolvedId} />
            </Grid>
          </>
        )}
        {/* Test History */}
        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" component="h2">
                {t('trackedPlaces.history.title')}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('trackedPlaces.history.filterBySource')}</InputLabel>
                <Select
                  // REV-580: without this MUI renders a zero-width space for an
                  // empty value, so the active default reads as 'no filter chosen'.
                  displayEmpty
                  value={sourceFilter}
                  label={t('trackedPlaces.history.filterBySource')}
                  onChange={(e) => {
                    setSourceFilter(e.target.value);
                    setHistoryPage(1);
                  }}
                >
                  <MenuItem value="">{t('trackedPlaces.history.allSources')}</MenuItem>
                  {[
                    'adhoc',
                    'ci',
                    'monitor',
                    'plugin',
                    'cli',
                    'scheduled',
                    'crawl',
                    'external',
                  ].map((src) => (
                    <MenuItem key={src} value={src}>
                      {t(`trackedPlaces.history.sourceTypes.${src}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {historyLoading ? (
              <Box>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : scanRuns.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" component="p" color="text.secondary" gutterBottom>
                  {t('trackedPlaces.history.empty')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('trackedPlaces.history.emptyAction')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<RunTestIcon />}
                  {...runTestLinkProps(context.status)}
                >
                  {t('trackedPlaces.history.runTest')}
                </Button>
              </Box>
            ) : (
              <>
                <ScrollableTableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('trackedPlaces.history.date')}</TableCell>
                        <TableCell>{t('trackedPlaces.history.source')}</TableCell>
                        <TableCell>{t('trackedPlaces.history.url')}</TableCell>
                        <TableCell>{t('trackedPlaces.history.status')}</TableCell>
                        <TableCell align="right">{t('trackedPlaces.history.findings')}</TableCell>
                        <TableCell align="right">{t('trackedPlaces.history.duration')}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scanRuns.map((run) => (
                        <TableRow key={run.id} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(run.completedAt || run.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={t(
                                `trackedPlaces.history.sourceTypes.${run.scanType}`,
                                run.scanType
                              )}
                              size="small"
                              variant="outlined"
                            />
                            {/* REV-813: the commit and branch, or PR, of a CI run. */}
                            <RunSourceSummary source={run.source} />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {run.targetUrl}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={t(`testing.status.${run.status}`)}
                              size="small"
                              color={getScanStatusColor(run.status)}
                            />
                            {/* REV-944: a crawl bot protection blocked, fully or partly. */}
                            <DiscoverySummary discovery={run.discovery} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {run.totalFindings}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {/* REV-939: the API's duration, not one re-derived
                                  here; an unfinished run still shows "-". */}
                              {run.completedAt ? formatDuration(run.duration) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {run.status === 'completed' && (
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={`/tests/${run.id}`}
                                aria-label={t('trackedPlaces.history.viewResults')}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollableTableContainer>
                {historyTotal > 10 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
                    <Button
                      size="small"
                      disabled={historyPage <= 1}
                      onClick={() => setHistoryPage((p) => p - 1)}
                    >
                      {t('common.previous', 'Previous')}
                    </Button>
                    <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                      {historyPage} / {Math.ceil(historyTotal / 10)}
                    </Typography>
                    <Button
                      size="small"
                      disabled={historyPage >= Math.ceil(historyTotal / 10)}
                      onClick={() => setHistoryPage((p) => p + 1)}
                    >
                      {t('common.next', 'Next')}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Scope Inventory Tab */}
      <TabPanel index={1} active={activeTab}>
        {orgId && resolvedId && <ScopeInventoryTab orgId={orgId} contextId={resolvedId} />}
      </TabPanel>

      {/* Audits Tab (REV-929). The resolved UUID, never the URL segment: the
          audit endpoints validate contextId as a UUID and reject a slug. */}
      <TabPanel index={2} active={activeTab}>
        {orgId && resolvedId && slug && (
          <AuditsTab orgId={orgId} contextId={resolvedId} slug={slug} orgRole={orgRole} />
        )}
      </TabPanel>

      {/* Integrate Tab */}
      <TabPanel index={3} active={activeTab}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('trackedPlaces.integrate.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('trackedPlaces.integrate.description')}
            </Typography>
            {/* REV-701: the API reference is served by revenant-api at /api/docs/
                (Swagger UI over /api/openapi.json), outside this SPA, so it is a
                plain anchor rather than a router link. */}
            <Typography variant="body2" sx={{ mb: 3 }}>
              {t('trackedPlaces.integrate.apiReference')}{' '}
              <Link href={API_REFERENCE_URL}>{t('trackedPlaces.integrate.apiReferenceLink')}</Link>
            </Typography>

            {/* Context ID */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('trackedPlaces.integrate.contextId')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={resolvedId || ''}
              slotProps={{
                input: {
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          copiedField === 'contextId' ? t('trackedPlaces.integrate.copied') : ''
                        }
                        open={copiedField === 'contextId'}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToClipboard(resolvedId || '', 'contextId')}
                          aria-label={t('trackedPlaces.integrate.contextId')}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
              helperText={t('trackedPlaces.integrate.contextIdHint')}
              sx={{ mb: 3 }}
            />

            {/* API Key notice.

                REV-700: this used to tell every reader to go and CREATE a key,
                including the many who already have one. Three states, because
                there are genuinely three: none yet, some already, and "the
                lookup did not answer" — where the honest thing is to say what
                is true either way rather than guess. */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {apiKeyCount === null
                  ? t(
                      'trackedPlaces.integrate.apiKeyRequired',
                      'Requests must be authenticated with an organization API key.'
                    )
                  : apiKeyCount === 0
                    ? t('trackedPlaces.integrate.apiKeyNeeded')
                    : t('trackedPlaces.integrate.apiKeyExisting', {
                        defaultValue_one: 'Authenticate requests with your organization API key.',
                        defaultValue_other:
                          'Authenticate requests with one of your organization API keys.',
                        count: apiKeyCount,
                      })}{' '}
                <Link component={RouterLink} to={`/orgs/${slug}/api-keys`}>
                  {t('trackedPlaces.integrate.apiKeyLink')}
                </Link>
              </Typography>
            </Alert>

            <Divider sx={{ my: 3 }} />

            {/* REV-702: say what these snippets DO before showing them.
                The walkthrough expected an example of requesting a test and
                found examples of submitting results, and read that as the
                examples being wrong. They are not — they are the only thing an
                API key can do. POST /api/v1/tests is guarded by `authenticate`
                (JWT only), while /api/v1/ingest uses `authenticateApiKey`, so
                CI genuinely cannot start a scan with a key. Setting that
                expectation up front is the fix available here; making it
                possible is a feature. */}
            <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
              <Typography variant="body2">{t('trackedPlaces.integrate.submitOnly')}</Typography>
            </Alert>

            {/* CI/CD snippet */}
            <Typography variant="subtitle1" component="h3" fontWeight={600} gutterBottom>
              {t('trackedPlaces.integrate.cicd.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('trackedPlaces.integrate.cicd.description')}
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.900',
                color: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                mb: 3,
              }}
            >
              {/* eslint-disable-next-line react/jsx-no-literals -- a code sample, copied verbatim; not prose */}
              {`# GitHub Actions example
- name: Run accessibility tests
  run: npx @afixt/afixt-engine --url "$URL" --format json > results.json

- name: Submit results to Revenant
  run: |
    curl -X POST ${window.location.origin}/api/v1/ingest \\
      -H "Authorization: Bearer $AFIXT_API_KEY" \\
      -H "Content-Type: application/json" \\
      -d '{
        "contextId": "${resolvedId}",
        "url": "$URL",
        "source": "ci",
        "results": { "findings": [...] },
        "sourceMetadata": {
          "commit_sha": "$GITHUB_SHA",
          "branch": "$GITHUB_REF_NAME"
        }
      }'`}
            </Box>

            {/* REV-555: the Monitor snippet block lived here. It advertised a
                /monitor.js browser agent that has never existed in either repo,
                so the tag resolved to the SPA's index.html and failed silently.
                Do not reinstate this block until monitor.js actually ships. */}

            {/* cURL example */}
            <Typography variant="subtitle1" component="h3" fontWeight={600} gutterBottom>
              {t('trackedPlaces.integrate.curl.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('trackedPlaces.integrate.curl.description')}
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.900',
                color: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
              }}
            >
              {/* eslint-disable-next-line react/jsx-no-literals -- a code sample, copied verbatim; not prose */}
              {`curl -X POST ${window.location.origin}/api/v1/ingest \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "contextId": "${resolvedId}",
    "url": "https://example.com",
    "source": "external",
    "results": {
      "findings": [
        {
          "ruleId": "img-alt",
          "severity": "critical",
          "message": "Image missing alt text",
          "selector": "img.hero"
        }
      ]
    }
  }'`}
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Settings Tab */}
      <TabPanel index={4} active={activeTab}>
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('trackedPlaces.settings.saved')}
          </Alert>
        )}

        {/* General Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('trackedPlaces.settings.general')}
            </Typography>
            <form onSubmit={handleSubmit(handleSave)}>
              <TextField
                {...register('name')}
                label={t('trackedPlaces.fields.name')}
                fullWidth
                margin="normal"
                error={!!formErrors.name}
                helperText={formErrors.name ? t(formErrors.name.message as string) : undefined}
                disabled={saving}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <Controller
                name="context_type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={t('trackedPlaces.fields.type')}
                    fullWidth
                    margin="normal"
                    error={!!formErrors.context_type}
                    // REV-810: the type decides what evidence this place
                    // accepts — by origin for a website or a deployment, by
                    // file and component for a repository — so the form says
                    // what each one means rather than leaving it to be guessed.
                    helperText={
                      formErrors.context_type
                        ? t(formErrors.context_type.message as string)
                        : t(`trackedPlaces.fields.typeHelpByType.${field.value}`)
                    }
                    disabled={saving}
                    slotProps={{ inputLabel: { shrink: true } }}
                  >
                    {typeOptions(context?.context_type).map((ct) => (
                      <MenuItem key={ct} value={ct}>
                        {t(`trackedPlaces.types.${ct}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <TextField
                {...register('canonical_identifier')}
                label={t('trackedPlaces.fields.identifier')}
                fullWidth
                margin="normal"
                error={!!formErrors.canonical_identifier}
                helperText={
                  formErrors.canonical_identifier
                    ? t(formErrors.canonical_identifier.message as string)
                    : t('trackedPlaces.fields.identifierHelp')
                }
                disabled={saving}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                {...register('allowed_origins')}
                label={t('trackedPlaces.fields.allowedOrigins')}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                error={!!formErrors.allowed_origins}
                helperText={
                  formErrors.allowed_origins
                    ? t(formErrors.allowed_origins.message as string, {
                        lines: invalidOriginLines(getValues('allowed_origins') ?? '').join(', '),
                        max: MAX_ALLOWED_ORIGINS,
                      })
                    : t('trackedPlaces.fields.allowedOriginsHelp')
                }
                disabled={saving}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                {...register('description')}
                label={t('trackedPlaces.fields.description')}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                error={!!formErrors.description}
                helperText={
                  formErrors.description ? t(formErrors.description.message as string) : undefined
                }
                disabled={saving}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t('trackedPlaces.settings.save')
                  )}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>

        {/* REV-699: below General, not above it. The walkthrough asked for
            this order and it is the right one: a tracked place's name, type
            and identifier are what the screen is primarily about, while the
            issue-tracker target is a secondary integration concern that only
            matters once the place itself is set up.

            REV-514: which repository THIS place's findings are exported to.
            The org Integrations tab authenticates; this chooses the target. */}
        {orgId && resolvedId && slug && (
          <IntegrationTargetCard orgId={orgId} orgSlug={slug} contextId={resolvedId} />
        )}

        {/* Test configuration (REV-461/462) — per-context overrides */}
        {orgId && resolvedId && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <ContextTestConfigSection orgId={orgId} contextId={resolvedId} />
            </CardContent>
          </Card>
        )}

        {/* Crawl settings (REV-1169) — hash routes + SPA render-settle window */}
        {orgId && resolvedId && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <ContextCrawlConfigSection orgId={orgId} contextId={resolvedId} />
            </CardContent>
          </Card>
        )}

        {/* Danger Zone */}
        <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
          <CardContent>
            <Typography variant="h6" component="h2" color="error" gutterBottom>
              {t('organizations.settings.danger')}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                {/* REV-561: variant="subtitle1" maps to <h6> in MUI's default
                    variantMapping, so this rendered an H6 directly under the
                    card's H2 — a skipped level. It IS the heading for this
                    action, so it becomes an H3 rather than being demoted to
                    plain text. */}
                <Typography variant="subtitle1" component="h3" fontWeight={600}>
                  {t('trackedPlaces.delete.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('trackedPlaces.delete.warning')}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setDeleteConfirmText('');
                  setDeleteDialogOpen(true);
                }}
              >
                {t('trackedPlaces.actions.delete')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteConfirmText('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle color="error">{t('trackedPlaces.delete.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('trackedPlaces.delete.permanentWarning')}
          </Alert>
          <DialogContentText sx={{ mb: 2 }}>
            {t('trackedPlaces.delete.confirmText')}
          </DialogContentText>
          <TextField
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={context.name}
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteConfirmText('');
            }}
            disabled={deleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={!canDelete || deleting}
          >
            {deleting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('trackedPlaces.delete.submit')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContextDetailPage;
